import qs from "qs";
import { toast } from "react-toastify";
import NEW_API from "../../Api/NewApi";
import { uploadFile } from "../../Utils";

/**
 * Administration de l'assistant Poseidon.
 *
 * Comme pour le chat, toutes les routes sont des routes custom : aucune route
 * Strapi par defaut n'est exposee pour les content-types ia_*. Les reponses sont
 * des objets plats.
 */

/* -------------------------------------------------------------------------- */
/*                                  Prompts                                   */
/* -------------------------------------------------------------------------- */

export const fetchIaPrompts = () => async (dispatch) => {
  dispatch({ type: "IA_ADMIN_PROMPTS_LOADING" });
  try {
    const { data } = await NEW_API.get("api/ia-prompts");
    dispatch({
      type: "IA_ADMIN_PROMPTS_SUCCESS",
      payload: { prompts: data?.data || [], meta: data?.meta || null },
    });
  } catch (error) {
    dispatch({ type: "IA_ADMIN_PROMPTS_FAIL", payload: error?.message });
  }
};

export const saveIaPrompt = (prompt, t) => async (dispatch) => {
  try {
    const payload = {
      data: {
        title: prompt.title,
        content: prompt.content,
        active: prompt.active,
        ordre: Number(prompt.ordre) || 0,
      },
    };

    if (prompt.id) {
      await NEW_API.put(`api/ia-prompt/${prompt.id}`, payload);
    } else {
      await NEW_API.post("api/ia-prompt", payload);
    }

    toast.success(t ? t("Saved") : "Enregistre");
    dispatch(fetchIaPrompts());
  } catch (error) {
    toast.error(
      error?.response?.data?.error?.message ||
        (t ? t("Save failed") : "Enregistrement impossible")
    );
  }
};

export const deleteIaPrompt = (id, t) => async (dispatch) => {
  try {
    await NEW_API.delete(`api/ia-prompt/${id}`);
    dispatch(fetchIaPrompts());
  } catch (error) {
    toast.error(t ? t("Deletion failed") : "Suppression impossible");
  }
};

/* -------------------------------------------------------------------------- */
/*                                 Documents                                  */
/* -------------------------------------------------------------------------- */

export const fetchIaDocuments = () => async (dispatch) => {
  dispatch({ type: "IA_ADMIN_DOCUMENTS_LOADING" });
  try {
    const { data } = await NEW_API.get("api/ia-documents");
    dispatch({ type: "IA_ADMIN_DOCUMENTS_SUCCESS", payload: data?.data || [] });
  } catch (error) {
    dispatch({ type: "IA_ADMIN_DOCUMENTS_FAIL", payload: error?.message });
  }
};

export const fetchIaCorpusStatus = () => async (dispatch) => {
  try {
    const { data } = await NEW_API.get("api/ia-corpus-status");
    dispatch({ type: "IA_ADMIN_STATUS_SUCCESS", payload: data?.data || null });
  } catch (error) {
    dispatch({ type: "IA_ADMIN_STATUS_FAIL", payload: error?.message });
  }
};

/**
 * Depose un PDF : televersement sur la plateforme puis indexation immediate
 * chez OpenAI, sans attendre le cron nocturne.
 */
export const uploadIaDocument = (file, title, t) => async (dispatch) => {
  dispatch({ type: "IA_ADMIN_UPLOADING", payload: true });
  try {
    // Le back revalide de toute facon : ce controle evite juste un aller-retour.
    const accepted = /\.(pdf|xlsx|xls|csv)$/i.test(file.name);
    if (!accepted) {
      toast.error(
        t ? t("Accepted formats: PDF, Excel or CSV") : "Formats acceptes : PDF, Excel ou CSV"
      );
      return;
    }

    const fileId = await uploadFile(file);
    await NEW_API.post("api/ia-upload-document", {
      data: { fileId, title: title || file.name },
    });

    toast.success(t ? t("Document added") : "Document ajoute");
    dispatch(fetchIaDocuments());
    dispatch(fetchIaCorpusStatus());
  } catch (error) {
    toast.error(t ? t("Upload failed") : "Ajout impossible");
  } finally {
    dispatch({ type: "IA_ADMIN_UPLOADING", payload: false });
  }
};

export const deleteIaDocument = (id, t) => async (dispatch) => {
  try {
    const { data } = await NEW_API.delete(`api/ia-document/${id}`);
    toast.success(
      data?.data?.status === "excluded"
        ? t
          ? t("Removed from the AI corpus (the PDF remains available)")
          : "Retire du corpus IA (le PDF reste disponible)"
        : t
        ? t("Document deleted")
        : "Document supprime"
    );
    dispatch(fetchIaDocuments());
    dispatch(fetchIaCorpusStatus());
    // Le resultat permet a l'ecran de confirmation de rester ouvert en cas
    // d'echec : l'erreur est deja signalee par un toast, mais fermer la fenetre
    // laisserait croire que la suppression a eu lieu.
    return true;
  } catch (error) {
    toast.error(t ? t("Deletion failed") : "Suppression impossible");
    return false;
  }
};

export const reindexIa = (iaDocumentId, t) => async (dispatch) => {
  dispatch({ type: "IA_ADMIN_REINDEXING", payload: true });
  try {
    await NEW_API.post("api/ia-reindex", {
      data: iaDocumentId ? { iaDocumentId } : {},
    });
    toast.success(t ? t("Reindexing done") : "Reindexation terminee");
    dispatch(fetchIaDocuments());
    dispatch(fetchIaCorpusStatus());
  } catch (error) {
    toast.error(t ? t("Reindexing failed") : "Reindexation impossible");
  } finally {
    dispatch({ type: "IA_ADMIN_REINDEXING", payload: false });
  }
};

/* -------------------------------------------------------------------------- */
/*                               Conversations                                */
/* -------------------------------------------------------------------------- */

export const fetchAdminIaDiscussions = () => async (dispatch) => {
  dispatch({ type: "IA_ADMIN_DISCUSSIONS_LOADING" });
  try {
    const { data } = await NEW_API.get("api/ia-admin-discussions");
    dispatch({ type: "IA_ADMIN_DISCUSSIONS_SUCCESS", payload: data?.data || [] });
  } catch (error) {
    dispatch({ type: "IA_ADMIN_DISCUSSIONS_FAIL", payload: error?.message });
  }
};

export const fetchAdminIaMessages = (discussionId) => async (dispatch) => {
  dispatch({ type: "IA_ADMIN_MESSAGES_LOADING", payload: discussionId });
  try {
    // Meme route que le chat : le handler autorise l'admin sur toute discussion.
    const { data } = await NEW_API.get(`api/ia-discussion/${discussionId}/messages`);
    dispatch({
      type: "IA_ADMIN_MESSAGES_SUCCESS",
      payload: { discussionId, messages: data?.data || [] },
    });
  } catch (error) {
    dispatch({ type: "IA_ADMIN_MESSAGES_FAIL", payload: error?.message });
  }
};

export const fetchIaStats = (from, to) => async (dispatch) => {
  try {
    const query = qs.stringify({ from, to }, { encodeValuesOnly: true, skipNulls: true });
    const { data } = await NEW_API.get(`api/ia-stats?${query}`);
    dispatch({ type: "IA_ADMIN_STATS_SUCCESS", payload: data?.data || null });
  } catch (error) {
    dispatch({ type: "IA_ADMIN_STATS_FAIL", payload: error?.message });
  }
};

export const closeAdminIaDiscussion = () => ({ type: "IA_ADMIN_MESSAGES_CLOSE" });
